/**
 * Menu action handlers — shared logic for canvas menu interactions.
 *
 * Used by canvasMouseMethods.ts (when user taps a menu hit area)
 * and by testRunner.ts (when test actions interact with menu items).
 */

import { appState, getCurrentCardState, setCardState } from './state'
import { CurrentAppState } from '../Models/AppState'
import { themes } from './themes'
import { assignCardCoordinates } from './assignCardCoordinates'
import { saveSettings } from './settingsManager'
import { schedulePaint } from './paint'
import { setButtonImages, loadCardBackImage, startNewGame } from './setupCards'
import { isPointInRect } from './getClickSelection'
import { getMenuHitAreas, type MenuHitArea, MenuScreen, setMenuScreen, resetMenuScreen } from './drawMenu'
import { showGameCenterDashboard } from './gameCenterManager'
import { fetchTipProducts, purchaseTip, resetPurchaseStatus } from './tipJarManager'

/**
 * Find the menu hit area (if any) at the given touch/click coordinates.
 * Returns the hit area or undefined if no hit.
 */
export function findMenuHitArea(x: number, y: number): MenuHitArea | undefined {
    const hitAreas = getMenuHitAreas()
    for (const area of hitAreas) {
        if (isPointInRect(x, y, area.x, area.y, area.width, area.height)) {
            return area
        }
    }
    return undefined
}

/**
 * Execute the action associated with a menu hit area ID.
 * Returns true if an action was executed, false if the ID was unrecognised.
 *
 * Theme changes are async (image reload) — fire and forget.
 */
export function executeMenuAction(hitAreaId: string): boolean {
    // ── Theme selection ─────────────────────────────────────────────────
    if (hitAreaId.startsWith('theme-')) {
        const themeName = hitAreaId.slice('theme-'.length)
        setTheme(themeName)
        return true
    }

    // ── Draw count ──────────────────────────────────────────────────────
    // Changing draw count starts a new game to avoid inconsistent stock/waste state
    if (hitAreaId === 'draw-1') {
        if (appState.settings.drawCount !== 1) {
            appState.NUM_CARDS_FLIPPED = 1
            appState.settings.drawCount = 1
            saveSettings(appState.settings)
            appState.currentAppState = CurrentAppState.Playing
            appState.showMenuRecords = false
            resetMenuScreen()
            startNewGame()
        }
        return true
    }
    if (hitAreaId === 'draw-3') {
        if (appState.settings.drawCount !== 3) {
            appState.NUM_CARDS_FLIPPED = 3
            appState.settings.drawCount = 3
            saveSettings(appState.settings)
            appState.currentAppState = CurrentAppState.Playing
            appState.showMenuRecords = false
            resetMenuScreen()
            startNewGame()
        }
        return true
    }

    // ── Auto-flip toggle ──────────────────────────────────────────────
    if (hitAreaId === 'flip-auto') {
        appState.settings.autoFlipCards = true
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }
    if (hitAreaId === 'flip-tap') {
        appState.settings.autoFlipCards = false
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }

    // ── Handedness ──────────────────────────────────────────────────────
    if (hitAreaId === 'hand-left') {
        applySettingWithRelayout(() => {
            appState.settings.handedness = 'left'
        })
        return true
    }
    if (hitAreaId === 'hand-right') {
        applySettingWithRelayout(() => {
            appState.settings.handedness = 'right'
        })
        return true
    }

    // ── Card back design ────────────────────────────────────────────────
    if (hitAreaId === 'back-pattern') {
        appState.settings.cardBackDesign = 'pattern'
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }
    if (hitAreaId === 'back-solid') {
        appState.settings.cardBackDesign = 'solid'
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }

    // ── Haptics ─────────────────────────────────────────────────────────
    if (hitAreaId === 'haptic-on') {
        appState.settings.hapticsEnabled = true
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }
    if (hitAreaId === 'haptic-off') {
        appState.settings.hapticsEnabled = false
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }

    // ── Sound ───────────────────────────────────────────────────────────
    if (hitAreaId === 'sound-on') {
        appState.settings.soundEnabled = true
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }
    if (hitAreaId === 'sound-off') {
        appState.settings.soundEnabled = false
        saveSettings(appState.settings)
        schedulePaint()
        return true
    }

    // ── New Game ─────────────────────────────────────────────────────────
    if (hitAreaId === 'new-game') {
        appState.currentAppState = CurrentAppState.Playing
        appState.showMenuRecords = false
        resetMenuScreen()
        startNewGame()
        schedulePaint()
        return true
    }

    // ── Fullscreen ──────────────────────────────────────────────────────
    if (hitAreaId === 'fullscreen') {
        toggleFullscreen()
        schedulePaint()
        return true
    }

    // ── Records toggle (menu leaderboard overlay) ───────────────────────
    if (hitAreaId === 'menu-records-toggle') {
        appState.showMenuRecords = !appState.showMenuRecords
        schedulePaint()
        return true
    }

    // ── Menu sub-screen navigation ──────────────────────────────────────
    if (hitAreaId === 'menu-themes') {
        setMenuScreen(MenuScreen.Themes)
        schedulePaint()
        return true
    }
    if (hitAreaId === 'menu-gamecenter') {
        showGameCenterDashboard()
        return true
    }
    if (hitAreaId === 'menu-tipjar') {
        setMenuScreen(MenuScreen.TipJar)
        resetPurchaseStatus()
        fetchTipProducts()
        schedulePaint()
        return true
    }
    if (hitAreaId === 'menu-back') {
        resetPurchaseStatus()
        resetMenuScreen()
        schedulePaint()
        return true
    }

    // ── Tip jar purchases ───────────────────────────────────────────────
    if (hitAreaId.startsWith('tip-')) {
        const productId = hitAreaId.slice('tip-'.length)
        purchaseTip(productId)
        return true
    }

    return false
}

// ─── Internal helpers ───────────────────────────────────────────────────────

/** Apply a settings change, save, reassign card coordinates, and repaint. */
function applySettingWithRelayout(apply: () => void): void {
    apply()
    saveSettings(appState.settings)
    const currentState = getCurrentCardState()
    const updatedState = assignCardCoordinates(currentState)
    setCardState(updatedState)
    schedulePaint()
}

/**
 * Set the active theme — reloads card images asynchronously.
 * Extracted from index.ts setTheme().
 */
function setTheme(themeName: string): void {
    const themeEntry = Object.entries(themes).find(([name]) => name === themeName)
    if (!themeEntry) {
        console.warn(`Theme ${themeName} not found`)
        return
    }

    const theme = themeEntry[1]

    // Update app state with new theme
    appState.currentTheme = theme
    appState.settings.themeName = themeName

    // Update CSS variables immediately
    document.documentElement.style.setProperty('--currentBackgroundColor', theme.backgroundColor)
    document.documentElement.style.setProperty('--currentHighlightColor', theme.highlightColor)

    // Update theme-color meta tag for iOS status bar and browser UI
    const themeColorMeta = document.getElementById('theme-color-meta') as HTMLMetaElement
    if (themeColorMeta) {
        themeColorMeta.content = theme.backgroundColor
    }

    // Reload button images for the new theme
    setButtonImages()

    // Save settings
    saveSettings(appState.settings)

    // Repaint immediately with the new theme (background, swatches, toggles)
    schedulePaint()

    // Reload card back image asynchronously (fire and forget)
    ;(async () => {
        await loadCardBackImage()
        schedulePaint()
    })()

    // Update iOS app icon to match theme (native bridge, no-op in browser)
    setNativeAppIcon(themeName)

    // Update browser tab favicon to match theme
    setFavicon(themeName)
}

/** Toggle browser fullscreen mode. */
function toggleFullscreen(): void {
    if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
    } else {
        document.documentElement.requestFullscreen().catch(() => {})
    }
}

/**
 * Tell iOS to switch the app icon to match the active theme.
 * Uses WKWebView message handler registered in ViewController.swift.
 * No-op in browsers / Android — the handler simply won't exist.
 */
function setNativeAppIcon(themeName: string): void {
    // Never trigger iOS icon-change prompts during automated scenario runs
    // (test runner / screenshot capture). Those system alerts can block input
    // and contaminate screenshots.
    const nativeScenario = (window as any).__nativeTestScenario
    const urlParams = new URLSearchParams(window.location.search)
    const isAutomatedRun =
        (typeof nativeScenario === 'string' && nativeScenario.trim().length > 0) ||
        urlParams.get('test') != null ||
        urlParams.get('screenshot') === '1'

    if (isAutomatedRun) {
        return
    }

    try {
        const wk = (window as any).webkit
        if (wk?.messageHandlers?.setAppIcon) {
            wk.messageHandlers.setAppIcon.postMessage(themeName)
        }
    } catch {
        // Silently ignore — not running in WKWebView
    }
}

/**
 * Update the browser tab favicon to the themed app icon SVG.
 * The SVGs live at ./res/app-icons/{ThemeName}.svg (copied from static/ at build time).
 */
export function setFavicon(themeName: string): void {
    const favicon = document.getElementById('favicon') as HTMLLinkElement | null
    if (favicon) {
        favicon.href = `./res/favicons/${themeName}.svg`
    }
}
